import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Contact";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { ReadingProgress } from "@/components/ReadingProgress";
import { ARTICLES, CONTACT } from "@/data/content";
import { ArrowLeft, ArrowUpRight, Linkedin, MessageCircle, Link2 } from "lucide-react";
import { toast } from "sonner";

const EASE = [0.22, 1, 0.36, 1];
const SHARE_CLS =
  "inline-flex items-center gap-2 rounded-full border border-[#0B2A5B]/20 text-[#0B2A5B] px-4 py-2 f-label text-xs uppercase tracking-[0.18em] hover:border-[#1E5FE0] hover:text-[#1E5FE0] transition-colors";

const ShareBar = ({ article }) => {
  const url = window.location.href;
  const text = encodeURIComponent(`${article.title} — Synergy Next`);
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };
  return (
    <div className="flex flex-wrap gap-3" data-testid="article-share-bar">
      <a className={SHARE_CLS} data-testid="article-share-whatsapp" target="_blank" rel="noopener noreferrer" href={`https://wa.me/?text=${text}%20${encodeURIComponent(url)}`}>
        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
      </a>
      <a className={SHARE_CLS} data-testid="article-share-linkedin" target="_blank" rel="noopener noreferrer" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}>
        <Linkedin className="w-3.5 h-3.5" /> LinkedIn
      </a>
      <button className={SHARE_CLS} data-testid="article-copy-link" onClick={copy}>
        <Link2 className="w-3.5 h-3.5" /> Copy link
      </button>
    </div>
  );
};

const ArticleSeo = ({ article }) => (
  <Seo
    title={`${article.title} — Synergy Next`}
    siteName="Synergy Next"
    description={article.excerpt}
    image={article.image}
    type="article"
    jsonLd={{
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      image: article.image,
      author: { "@type": "Organization", name: "Synergy Next" },
      publisher: { "@type": "Organization", name: "Synergy Next", logo: { "@type": "ImageObject", url: "/images/logo.jpeg" } },
    }}
  />
);

const ArticleHeader = ({ article }) => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 lg:pt-20">
    <Link to="/#insights" data-testid="article-back-link" className="inline-flex items-center gap-2 f-label text-[13px] uppercase tracking-[0.2em] text-[#0B2A5B]/60 hover:text-[#1E5FE0] transition-colors">
      <ArrowLeft className="w-4 h-4" /> Back to insights
    </Link>
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }} className="mt-10 max-w-4xl">
      <Eyebrow>Our Insights</Eyebrow>
      <h1 data-testid="article-title" className="f-display text-3xl sm:text-4xl lg:text-6xl text-[#0B2A5B] tracking-tight mt-4 leading-[1.08]">
        {article.title}
      </h1>
      <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">{article.excerpt}</p>
      <div className="mt-8">
        <ShareBar article={article} />
      </div>
    </motion.div>
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.2, delay: 0.2, ease: EASE }}
      className="mt-12 rounded-[2rem] overflow-hidden aspect-[16/9] sm:aspect-[21/9] shadow-[0_30px_60px_-30px_rgba(11,42,91,0.4)]"
    >
      <img src={article.image} alt={article.title} data-testid="article-hero-image" className="w-full h-full object-cover" />
    </motion.div>
  </section>
);

const ArticleBody = ({ paragraphs }) => (
  <article data-testid="article-body" className="lg:col-span-8 space-y-7">
    {paragraphs.map((p, i) => (
      <Reveal key={p} y={20}>
        <p className={`text-[17px] leading-[1.9] text-slate-700 ${i === 0 ? "first-letter:f-display first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85] first-letter:text-[#1E5FE0]" : ""}`}>
          {p}
        </p>
      </Reveal>
    ))}
  </article>
);

const ArticleAside = ({ related }) => (
  <aside className="lg:col-span-4 lg:pl-6">
    <Reveal delay={0.1}>
      <div className="lg:sticky lg:top-28 space-y-6">
        <div className="rounded-2xl bg-[#0B2A5B] text-white p-8">
          <p className="f-label text-xs uppercase tracking-[0.2em] text-[#7FA8FF]">Start a conversation</p>
          <p className="f-display text-2xl mt-3 leading-snug">Does this resonate with your organisation?</p>
          <a
            href={CONTACT.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="article-whatsapp-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-[#0B2A5B] px-5 py-3 f-label text-[13px] uppercase tracking-[0.15em] hover:bg-[#1E5FE0] hover:text-white transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp us
          </a>
        </div>
        <div>
          <p className="f-label text-xs uppercase tracking-[0.2em] text-[#0B2A5B]/50 mb-4">More perspectives</p>
          <div className="space-y-3">
            {related.map((r) => (
              <Link key={r.id} to={`/insights/${r.id}`} data-testid={`article-related-${r.id}`} className="group flex items-start justify-between gap-4 rounded-xl border border-[#0B2A5B]/10 bg-white p-5 hover:border-[#1E5FE0]/50 transition-colors">
                <span className="f-display text-base text-[#0B2A5B] leading-snug group-hover:text-[#1E5FE0] transition-colors">{r.title}</span>
                <ArrowUpRight className="w-4 h-4 shrink-0 text-[#0B2A5B]/30 group-hover:text-[#1E5FE0]" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Reveal>
  </aside>
);

export default function ArticlePage() {
  const { id } = useParams();
  const article = ARTICLES.find((a) => a.id === id);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [id]);

  if (!article) return <Navigate to="/" replace />;
  const related = ARTICLES.filter((a) => a.id !== id).slice(0, 3);

  return (
    <div className="grain bg-[#FAFAF5] min-h-screen">
      <ArticleSeo article={article} />
      <ReadingProgress />
      <Nav />
      <main data-testid="article-page" className="pt-[70px]">
        <ArticleHeader article={article} />
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <ArticleBody paragraphs={article.paragraphs} />
          <ArticleAside related={related} />
        </section>
      </main>
      <Footer />
    </div>
  );
}
